import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import Barcode from "react-barcode";

//import UserBcCouponDetail from "./UserBcCouponDetail";
import CouponItemDetail from "../coupon/CouponItemDetail"; // ปรับ path ตามจริง

import Modal from "../common/Modal";
import ToggleMenu from "../common/ToggleMenu";
import ProfileWithBackButtonHeader from "../header/ProfileWithBackButtonHeader";

import { useLiffProfile } from "../../context/LiffProfileContext";
import { MaskInputHelper } from "../helpers/mask-input-helper";
import LoadingBar from "../LoadingBar";

import axios from "axios";

const MyCouponDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { authToken, userId } = useLiffProfile();

  const [couponData, setCouponData] = useState(null);
  const [couponCode, setCouponCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [codeType, setCodeType] = useState(0); // 0 = barcode, 1 = QR
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(false);

  // เพิ่ม state ใหม่
  const [viewTab, setViewTab] = useState(0); // 0 = Use, 1 = Share
  const [copied, setCopied] = useState(false); // สำหรับ tooltip "คัดลอกแล้ว"

  const intervalRef = useRef(null);
  const lang = i18n.language.toUpperCase();
  const seniorLayout = localStorage.getItem("seniorLayout") === "true";

  const formatDate = (date) => {
    if (!date) return "";
    return new Intl.DateTimeFormat(lang === "EN" ? "en-US" : "th-TH", {
      dateStyle: "short",
    }).format(new Date(date));
  };

  const couponTimeout =
    overlayVisible && couponData?.couponValidation === "TP" && !timeRemaining;

  const clientAuth = process.env.REACT_APP_CLIENT_AUTH;

  const headers = {
    Authorization: `Bearer ${authToken}`,
    "X-Client-Auth": clientAuth,
    "x-user-id": userId,
    "Accept-Language": lang,
  };

  useEffect(() => {
    const fetchCouponDetail = async () => {
      try {
        setLoading(true);
        const apiUrl = `${process.env.REACT_APP_API_URL}/api/users/current/clm-coupons/${id}`;

        const response = await axios.get(apiUrl, {
          headers: headers,
        });

        const coupon = response.data?.data;
        if (coupon) {
          setCouponData(coupon);

          // ✅ GA4 Event - My Coupon Detail View
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "my_coupon_detail_view",
            user_id: userId,
            coupon_id: id,
            coupon_name: coupon.name,
            attribute_type: coupon.attributeType,
          });

          if (coupon.usable) {
            const apiUrlCode = `${process.env.REACT_APP_API_URL}/api/users/current/clm-coupons/${id}/use`;
            const responseCode = await axios.post(
              apiUrlCode,
              {},
              {
                headers: headers,
              }
            );

            const couponCodeData = responseCode.data?.data;
            if (couponCodeData?.responseCode === "000") {
              setCouponCode(couponCodeData.code);
            } else {
              setCouponCode(null);
            }
          }

          if (coupon.couponValidation === "TP") {
            const useDate = coupon.useDate ? moment(coupon.useDate) : moment();
            const timeoutAt = useDate.add(coupon.usageTimeout ?? 15, "minutes");

            intervalRef.current = setInterval(() => {
              const duration = moment.duration(timeoutAt.diff(moment()));
              if (duration.asMilliseconds() > 0) {
                setTimeRemaining(MaskInputHelper.formatRemainingTime(duration));
              } else {
                clearInterval(intervalRef.current);
                setTimeRemaining(null);
              }
              setOverlayVisible(true);
            }, 1000);
          }
        } else {
          setError("No data found in response.");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCouponDetail();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, authToken, userId]);

  const handleExitConfirmed = (couponType) => {
    navigate(`/my-coupons?couponType=${couponType || ""}`);
  };

  const shareToLine = () => {
    const text = encodeURIComponent("ลองแชร์หน้านี้ดูสิ!");
    const url = encodeURIComponent(window.location.href);
    const lineShareUrl = `https://social-plugins.line.me/lineit/share?url=${url}&text=${text}`;
    window.open(lineShareUrl, "_blank");
  };

  // ✅ ใหม่: คัดลอกลิงก์
  const handleCopyLink = async (link, couponData) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "click_copy_download_link",
      user_id: userId,
      coupon_id: id,
      coupon_name: couponData.name,
      attribute_type: couponData.attributeType,
    });
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // เผื่อ clipboard ไม่ได้สิทธิ์
      const textarea = document.createElement("textarea");
      textarea.value = link;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  // ✅ ใหม่: Share แบบระบุคนผ่าน LINE (ต้องอยู่ใน LIFF และเปิดสิทธิ์ shareTargetPicker)
  const handleShareToFriend = async (link, couponData) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "click_share_to_line_friend",
      user_id: userId,
      coupon_id: id,
      coupon_name: couponData.name,
      attribute_type: couponData.attributeType,
    });
    try {
      const liff = window.liff;
      const shareText =
        (t("share_coupon_message") || "แชร์คูปองให้เพื่อน") + "\n" + link;

      if (liff && liff.isLoggedIn && liff.isLoggedIn()) {
        if (liff.isApiAvailable && liff.isApiAvailable("shareTargetPicker")) {
          await liff.shareTargetPicker([
            {
              type: "text",
              text: shareText,
            },
          ]);
          return;
        }
      }
      // Fallback: เปิดแชร์ข้อความปกติ (ระบุคนไม่ได้)
      window.open(
        `https://line.me/R/msg/text/?${encodeURIComponent(shareText)}`,
        "_blank"
      );
    } catch (e) {
      // Fallback ถ้า error
      window.open(
        `https://line.me/R/msg/text/?${encodeURIComponent(link)}`,
        "_blank"
      );
    }
  };
  // ด้านบนหลังประกาศ couponData
  const hasDownload = !!(
    couponData?.linkDownload && String(couponData.linkDownload).trim()
  );

  //console.log(hasDownload);

  return (
    <div>
      <ProfileWithBackButtonHeader
        onClick={() => {
          if (intervalRef.current) setShowConfirmModal(true);
          else handleExitConfirmed(couponData?.couponType);
        }}
      />

      {couponData && (
        <CouponItemDetail userCoupon={true} value={couponData}>
          {couponData.usable ? (
            <>
              {/* --- กรณีมีลิงก์ดาวน์โหลด --- */}
              {hasDownload ? (
                <>
                  {/* แท็บ Use / Share */}
                  <ToggleMenu
                    names={[
                      t("use_coupon") || "Use",
                      t("share_coupon") || "Share",
                    ]}
                    value={viewTab}
                    onClick={setViewTab}
                    className={
                      seniorLayout
                        ? "text-22 mb-3"
                        : "text-lg sm:text-base mb-3"
                    }
                  />
                  {viewTab === 0 ? (
                    // TAB: Use -> แสดงปุ่มดาวน์โหลด (พร้อมลิงก์บนปุ่ม)
                    <div className="bg-secondary-background flex flex-col items-center rounded-2xl border border-overlay-12 px-10 py-5 gap-4">
                      <div className="w-full relative flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center items-center">
                        <a
                          href={couponData.linkDownload}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-primary text-white rounded-xl px-6 py-6 font-semibold text-center leading-tight"
                          title={couponData.linkDownload}
                          onClick={() => {
                            window.dataLayer = window.dataLayer || [];
                            window.dataLayer.push({
                              event: "click_download_sticker",
                              user_id: userId,
                              coupon_id: id,
                              coupon_name: couponData.name,
                              attribute_type: couponData.attributeType,
                            });
                          }}
                        >
                          <div className="flex flex-col items-center">
                            <span>{t("download_sticker")}</span>
                            {/*<span className="text-xs opacity-90 break-all max-w-[260px]">
                              {couponData.linkDownload}
                            </span>*/}
                          </div>
                        </a>
                      </div>
                      <div
                        className={`font-normal text-secondary-text-gray pt-1 ${
                          seniorLayout ? "text-lg" : "text-sm"
                        }`}
                      >
                        {`${t("expiry_date")} ${formatDate(
                          couponData?.expireDate
                        )}`}
                      </div>
                    </div>
                  ) : (
                    // TAB: Share -> กล่องลิงก์ + Copy + Share to LINE
                    <div className="bg-secondary-background flex flex-col items-center rounded-2xl border border-overlay-12 px-10 py-5 gap-4">
                      <div className="w-full max-w-xl">
                        <div className="w-full rounded-lg border border-overlay-12 px-3 py-2 bg-white text-sm break-all">
                          {couponData.linkDownload}
                        </div>
                      </div>
                      {copied && (
                        <div
                          className={`font-normal text-secondary-text-gray pt-1 ${
                            seniorLayout ? "text-lg" : "text-sm"
                          }`}
                        >
                          <span className="text-sm text-green-600">
                            {t("copied") || "คัดลอกแล้ว"}
                          </span>
                        </div>
                      )}
                      <div className="flex gap-3 items-center">
                        <button
                          onClick={() =>
                            handleCopyLink(couponData.linkDownload, couponData)
                          }
                          className="px-4 py-2 rounded-lg border border-overlay-12 bg-white font-medium"
                        >
                          {t("copy_link") || "คัดลอกลิงก์"}
                        </button>
                        <button
                          onClick={() =>
                            handleShareToFriend(
                              couponData.linkDownload,
                              couponData
                            )
                          }
                          className="px-4 py-2 rounded-lg bg-primary text-white font-semibold"
                        >
                          {t("share_to_friend_on_line") ||
                            "แชร์ให้เพื่อนใน LINE"}
                        </button>
                      </div>
                      <div
                        className={`font-normal text-secondary-text-gray pt-1 ${
                          seniorLayout ? "text-lg" : "text-sm"
                        }`}
                      >
                        {`${t("expiry_date")} ${formatDate(
                          couponData?.expireDate
                        )}`}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* --- กรณีไม่มีลิงก์ดาวน์โหลด --- */
                <>
                  <ToggleMenu
                    names={[t("barcode"), t("qr_code")]}
                    value={codeType}
                    onClick={setCodeType}
                    className={
                      seniorLayout ? "text-22" : "text-lg sm:text-base"
                    }
                  />
                  <div className="bg-secondary-background flex flex-col items-center rounded-2xl border border-overlay-12 px-10 py-5">
                    <div className="w-full relative flex justify-center items-center">
                      {codeType === 0 && couponCode && (
                        <Barcode
                          value={couponCode}
                          width={2}
                          height={80}
                          displayValue={false}
                        />
                      )}
                      {codeType === 1 && couponCode && (
                        <QRCodeCanvas value={couponCode} size={184} level="H" />
                      )}
                      {couponTimeout && (
                        <div className="w-full h-full flex justify-center items-center absolute z-40 bg-overlay">
                          <span
                            className={`w-full bg-overlay text-secondary-text-invert rounded-xl text-center py-1 ${
                              seniorLayout ? "text-3xl" : "text-2xl"
                            }`}
                          >
                            {t("used")}
                          </span>
                        </div>
                      )}
                    </div>
                    <div
                      className={`w-full font-medium text-center ${
                        seniorLayout ? "text-xl" : "text-base"
                      }`}
                    >
                      {couponCode}
                    </div>
                    <div
                      className={`font-normal text-secondary-text-gray pt-2 ${
                        seniorLayout ? "text-lg" : "text-sm"
                      }`}
                    >
                      {`${t("expiry_date")} ${formatDate(
                        couponData?.expireDate
                      )}`}
                    </div>
                  </div>

                  {timeRemaining && (
                    <div
                      className={`w-full font-normal text-center ${
                        seniorLayout ? "text-xl" : "text-base"
                      }`}
                    >
                      <span>{t("time_remaining")} </span>
                      <span className="text-danger">{timeRemaining}</span>
                      <span> {t("minutes")}</span>
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            // ไม่ usable: แสดงข้อความขอบคุณเดิม
            <div className="font-medium text-primary text-center pt-5">
              <span className={seniorLayout ? "text-3xl" : "text-2xl"}>
                {couponData?.useDescKey === "THX"
                  ? t("thank_to_join_event")
                  : ""}
              </span>
            </div>
          )}
        </CouponItemDetail>
      )}

      <Modal
        show={showConfirmModal}
        buttons={[]}
        closable={false}
        containerClass="sm:pt-5 pt-8"
        acceptButton={t("confirm")}
        denyButton={t("cancel")}
        onAccept={() => {
          setShowConfirmModal(false);
          handleExitConfirmed(couponData?.couponType);
        }}
        onDeny={() => setShowConfirmModal(false)}
      >
        <div className="w-full h-40 flex flex-col items-center gap-4">
          <div className="text-xl font-medium">
            {t("confirm_ending_coupon_usage_header")}
          </div>
          <div className="text-sm font-normal">
            {t("confirm_ending_coupon_usage_desc")}
          </div>
        </div>
      </Modal>
      <LoadingBar loading={loading} label="Loading..." />
    </div>
  );
};

export default MyCouponDetail;
